import { apiUrl } from '../../../shared/api/apiConfig';
import {
  calculateLoadTotalWeight,
  getReadingDeviceKind,
  isAcceptedReading,
  isCompletedRequest,
  normalizeBinding,
  normalizeDevice,
  normalizeReading,
  normalizeRequest,
  normalizeRequestLoad,
  sortBindings,
  sortDevices,
  sortReadings,
  sortRequestLoads,
  sortRequests,
} from './kioskTransforms';

const inFlightGetRequests = new Map();

function shareInFlightGet(key, operation) {
  const existing = inFlightGetRequests.get(key);
  if (existing) {
    return existing;
  }

  const request = Promise.resolve().then(operation);
  inFlightGetRequests.set(key, request);

  const clear = () => {
    if (inFlightGetRequests.get(key) === request) {
      inFlightGetRequests.delete(key);
    }
  };
  request.then(clear, clear);
  return request;
}

async function readJson(response) {
  return response.json().catch(() => ({}));
}

async function readJsonOrThrow(response, fallbackMessage) {
  try {
    return await response.json();
  } catch {
    throw new Error(fallbackMessage);
  }
}

async function readErrorMessage(response, fallbackMessage) {
  const errorBody = await readJson(response);
  const message = errorBody.message ?? fallbackMessage;
  const upstreamResponse = typeof errorBody.upstreamResponse === 'string'
    ? errorBody.upstreamResponse.trim()
    : '';

  return upstreamResponse
    ? `${message} : ${upstreamResponse}`
    : message;
}

function createAbortError(message) {
  return new DOMException(message, 'AbortError');
}

function isAbortError(error) {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function fetchKioskSetupData() {
  return shareInFlightGet('kiosk-setup', loadKioskSetupData);
}

async function loadKioskSetupData() {
  const [bindingsResponse, devicesResponse, requestLoadsResponse] = await Promise.all([
    fetch(apiUrl('/registerbindings')),
    fetch(apiUrl('/devices')),
    fetch(apiUrl('/requestloads/summaries?activeOnly=true')),
  ]);

  if (!bindingsResponse.ok || !devicesResponse.ok || !requestLoadsResponse.ok) {
    throw new Error('ไม่สามารถโหลดข้อมูลสถานีได้.');
  }

  const [bindingsPayload, devicesPayload, requestLoadsPayload] = await Promise.all([
    readJsonOrThrow(bindingsResponse, 'ไม่สามารถโหลดข้อมูลสถานีได้.'),
    readJsonOrThrow(devicesResponse, 'ไม่สามารถโหลดข้อมูลสถานีได้.'),
    readJsonOrThrow(requestLoadsResponse, 'ไม่สามารถโหลดข้อมูลสถานีได้.'),
  ]);

  if (
    !Array.isArray(bindingsPayload)
    || !Array.isArray(devicesPayload)
    || !Array.isArray(requestLoadsPayload)
  ) {
    throw new Error('ไม่สามารถโหลดข้อมูลสถานีได้.');
  }

  return {
    bindings: sortBindings(bindingsPayload ?? []).map(normalizeBinding),
    devices: sortDevices((devicesPayload ?? []).map(normalizeDevice)),
    requests: [],
    requestLoads: sortRequestLoads((requestLoadsPayload ?? []).map((requestLoad) => normalizeRequestLoad(requestLoad))),
  };
}

export function fetchRequestLoadSummaries({ activeOnly = false, tpnBookingIds = [] } = {}) {
  const params = new URLSearchParams();
  if (activeOnly) {
    params.set('activeOnly', 'true');
  }
  tpnBookingIds.forEach((bookingId) => params.append('tpnBookingIds', bookingId));
  const requestUrl = apiUrl(`/requestloads/summaries?${params}`);

  return shareInFlightGet(requestUrl, async () => {
    const response = await fetch(requestUrl);
    if (!response.ok) {
      throw new Error('Unable to refresh load progress.');
    }

    const payload = await readJsonOrThrow(response, 'Unable to refresh load progress.');
    if (!Array.isArray(payload)) {
      throw new Error('Unable to refresh load progress.');
    }

    return sortRequestLoads(payload.map((requestLoad) => normalizeRequestLoad(requestLoad)));
  });
}

export function fetchActiveRequestLoadSummaries() {
  return fetchRequestLoadSummaries({ activeOnly: true });
}

export async function fetchTpnStockProductDetails(palletId, options = {}) {
  const { signal, timeoutMs = 10000 } = options;
  const normalizedPalletId = String(palletId ?? '').trim();
  if (!normalizedPalletId) {
    return [];
  }

  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => {
    controller.abort(createAbortError('Product lookup timed out.'));
  }, timeoutMs);
  const handleAbort = () => {
    controller.abort(signal?.reason ?? createAbortError('The operation was aborted.'));
  };

  if (signal) {
    if (signal.aborted) {
      globalThis.clearTimeout(timeoutId);
      throw signal.reason ?? createAbortError('The operation was aborted.');
    }

    signal.addEventListener('abort', handleAbort, { once: true });
  }

  let response;
  try {
    response = await fetch(apiUrl(`/tpnstock/pallets/${encodeURIComponent(normalizedPalletId)}`), {
      signal: controller.signal,
    });
  } catch (error) {
    if (isAbortError(error) && !signal?.aborted) {
      throw new Error('Product lookup timed out.');
    }

    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', handleAbort);
  }

  if (response.status === 404) {
    return [];
  }

  if (!response.ok) {
    throw new Error('Unable to load pallet product details.');
  }

  const payload = await response.json().catch(() => null);
  const matchedItems = payload ? [payload] : [];

  return matchedItems.map((item) => ({
    canonicalProductId: String(item?.canonicalProductId ?? '').trim(),
    palletTypeId: String(item?.palletTypeId ?? '').trim(),
    productName: String(item?.productName ?? '').trim(),
    brandName: String(item?.brandName ?? '').trim(),
    size: String(item?.size ?? '').trim(),
    calculatedGoodsWeightKg: item?.calculatedGoodsWeightKg == null
      ? null
      : Number(item.calculatedGoodsWeightKg),
  }));
}

export async function fetchTpnStockProductNames(palletId, options = {}) {
  const products = await fetchTpnStockProductDetails(palletId, options);
  return [...new Set(products.map((product) => product.productName).filter(Boolean))];
}

export function fetchActiveRequestForRegister(registerId) {
  const requestUrl = apiUrl(`/weighingrequests/active/register/${encodeURIComponent(registerId)}`);
  return shareInFlightGet(requestUrl, async () => {
    const response = await fetch(requestUrl);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('ไม่สามารถโหลดคำขอการชั่งปัจจุบันสำหรับสถานีนี้ได้.');
    }

    return normalizeRequest(await response.json());
  });
}

export function fetchRequestReadings(requestId) {
  const requestUrl = apiUrl(`/requestreadings/request/${encodeURIComponent(requestId)}`);
  return shareInFlightGet(requestUrl, async () => {
    const response = await fetch(requestUrl);

    if (!response.ok) {
      throw new Error('ไม่สามารถโหลดข้อมูลการชั่งสำหรับคำขอที่เลือกได้.');
    }

    const payload = await response.json();
    return sortReadings((Array.isArray(payload) ? payload : []).map(normalizeReading));
  });
}

export async function submitCameraQrReading(requestId, readingValue) {
  const response = await fetch(apiUrl('/requestreadings/camera'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      requestId,
      readingValue,
      occurredAtUtc: new Date().toISOString(),
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Unable to submit the camera QR reading.'));
  }

  return normalizeReading(await readJsonOrThrow(
    response,
    'The camera QR reading response was invalid.',
  ));
}

export async function lookupScaleWeightFromQr(requestId) {
  const response = await fetch(apiUrl('/requestreadings/qr-weight'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requestId }),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Unable to look up the weight from the scanned QR.'));
  }

  return normalizeReading(await readJsonOrThrow(
    response,
    'The QR weight lookup response was invalid.',
  ));
}

export async function submitManualScaleWeight(requestId, weightKg) {
  const response = await fetch(apiUrl('/requestreadings/manual-weight'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requestId, weightKg }),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'ไม่สามารถบันทึกน้ำหนักที่กรอกได้'));
  }

  return normalizeReading(await readJsonOrThrow(
    response,
    'ผลลัพธ์การบันทึกน้ำหนักไม่ถูกต้อง',
  ));
}

export function fetchRequestLoad(loadId, fallbackRegisterId = '') {
  const requestUrl = apiUrl(`/requestloads/${encodeURIComponent(loadId)}`);
  const requestKey = `${requestUrl}|register:${fallbackRegisterId}`;
  return shareInFlightGet(requestKey, async () => {
    const response = await fetch(requestUrl);

    if (!response.ok) {
      throw new Error('ไม่สามารถโหลดรายละเอียดการชั่งน้ำหนักได้.');
    }

    return normalizeRequestLoad(await response.json(), fallbackRegisterId);
  });
}

export async function fetchRequestLoadSummaryRequests(requests, deviceMap) {
  return Promise.all(requests.map(async (request) => {
    if (
      request.palletValue !== undefined
      || request.palletProductName !== undefined
      || request.scaleValue !== undefined
      || request.numericScaleValue !== undefined
    ) {
      const parsedValue = Number.parseFloat(request.scaleValue ?? '');
      return {
        ...request,
        palletValue: request.palletValue ?? 'Pending',
        palletProductName: request.palletProductName ?? '',
        palletWeight: Number.isFinite(request.palletWeight)
          ? request.palletWeight
          : 0,
        palletQuantity: Number.isFinite(request.palletQuantity)
          ? request.palletQuantity
          : 0,
        calculatedGoodsWeightKg: Number.isFinite(request.calculatedGoodsWeightKg)
          ? request.calculatedGoodsWeightKg
          : null,
        scaleValue: request.scaleValue ?? 'Pending',
        numericScaleValue: Number.isFinite(request.numericScaleValue)
          ? request.numericScaleValue
          : Number.isFinite(parsedValue) ? parsedValue : 0,
      };
    }

    const hasSelectedReadings = Boolean(request.selectedScannerReadingId || request.selectedScaleReadingId);
    const readingSourceId = !hasSelectedReadings && request.readingsSourceRequestId
      ? request.readingsSourceRequestId
      : request.requestId;
    const readings = await fetchRequestReadings(readingSourceId);
    const acceptedScannerReading = request.selectedScannerReadingId
      ? readings.find((reading) => reading.id === request.selectedScannerReadingId)
      : readings.find((reading) => (
        isAcceptedReading(reading) && getReadingDeviceKind(reading, deviceMap ?? new Map()) === 'Scanner'
      ));
    const acceptedScaleReading = request.selectedScaleReadingId
      ? readings.find((reading) => reading.id === request.selectedScaleReadingId)
      : readings.find((reading) => (
        isAcceptedReading(reading) && getReadingDeviceKind(reading, deviceMap ?? new Map()) === 'Scale'
      ));
    const parsedValue = Number.parseFloat(acceptedScaleReading?.readingValue ?? '');

    return {
      ...request,
      palletValue: acceptedScannerReading?.readingValue ?? 'Pending',
      palletProductName: acceptedScannerReading?.scannedProduct?.name
        ?? acceptedScannerReading?.scannedProductName
        ?? '',
      palletCanonicalProductId: acceptedScannerReading?.scannedProduct?.canonicalProductId ?? null,
      palletWeight: Number.isFinite(acceptedScannerReading?.scannedProduct?.palletWeight)
        ? acceptedScannerReading.scannedProduct.palletWeight
        : 0,
      palletQuantity: Number.isFinite(acceptedScannerReading?.scannedProduct?.quantity)
        ? acceptedScannerReading.scannedProduct.quantity
        : 0,
      calculatedGoodsWeightKg: Number.isFinite(acceptedScannerReading?.scannedProduct?.calculatedGoodsWeightKg)
        ? acceptedScannerReading.scannedProduct.calculatedGoodsWeightKg
        : null,
      scaleValue: acceptedScaleReading?.readingValue ?? 'Pending',
      numericScaleValue: Number.isFinite(parsedValue) ? parsedValue : 0,
    };
  }));
}

export function buildRequestLoadSummaryFromRequests(requestDetails) {
  const completedRequestDetails = requestDetails.filter(isCompletedRequest);
  const totalWeight = calculateLoadTotalWeight(completedRequestDetails);

  return {
    requestCount: completedRequestDetails.length,
    totalWeight,
    requests: requestDetails,
    status: 'success',
  };
}

export async function createWeighingRequest(payload) {
  const response = await fetch(apiUrl('/weighingrequests'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Unable to create the station request.'));
  }

  return normalizeRequest(await response.json());
}

export async function createRequestLoad(payload, fallbackRegisterId = '') {
  const response = await fetch(apiUrl('/requestloads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'ไม่สามารถสร้างรอบการขนส่งได้.'));
  }

  return normalizeRequestLoad(await response.json(), fallbackRegisterId);
}

export async function fetchRequestLoadDetail(loadId) {
  const response = await fetch(apiUrl(`/requestloads/${encodeURIComponent(loadId)}/detail`));

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Unable to load request load detail.'));
  }

  const detail = normalizeRequestLoad(await response.json());
  const requests = sortRequests((detail.weighingRequests ?? []).map(normalizeRequest));
  const enrichedRequests = await fetchRequestLoadSummaryRequests(requests, new Map());

  return {
    detail,
    requests: enrichedRequests,
  };
}

export async function updateRequestLoadStatus(loadId, action, updatedBy) {
  const response = await fetch(apiUrl(`/requestloads/${encodeURIComponent(loadId)}/${action}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updatedBy }),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, `Unable to ${action} the load.`));
  }

  return normalizeRequestLoad(await response.json());
}

export async function confirmWeighingRequest(requestId, payload) {
  const response = await fetch(apiUrl(`/weighingrequests/${encodeURIComponent(requestId)}/confirm`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'ไม่สามารถยืนยันการชั่งได้.'));
  }

  return normalizeRequest(await response.json());
}

export async function rejectWeighingRequest(requestId, payload) {
  const response = await fetch(apiUrl(`/weighingrequests/${encodeURIComponent(requestId)}/reject`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'ไม่สามารถปฏิเสธการชั่งได้.'));
  }

  return normalizeRequest(await response.json());
}

export async function voidWeighingRequest(requestId, payload) {
  const response = await fetch(apiUrl(`/weighingrequests/${encodeURIComponent(requestId)}/void`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Unable to void the request.'));
  }

  const data = await response.json();
  return {
    voided: normalizeRequest(data.voided),
    replacement: normalizeRequest(data.replacement),
  };
}

export async function updateSingleReadingMode(registerId, enabled) {
  const response = await fetch(apiUrl(`/registerbindings/${encodeURIComponent(registerId)}/single-reading-mode`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ singleReadingMode: enabled }),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Unable to update single reading mode.'));
  }

  return normalizeBinding(await response.json());
}
